import { TREND_TIME_TICK_SECONDS } from '../constants';
import type { ChartRow } from '../types';

export const formatSecondLabel = (second: number) => {
  const minutes = Math.floor(second / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (second % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const buildTrendTimeTicks = (
  rows: Array<{ second?: number | string | null }>
) => {
  if (!rows.length) return [];

  const seconds = rows.map(row => Number(row.second)).filter(Number.isFinite);
  if (!seconds.length) return [];

  const minSecond = Math.min(...seconds);
  const maxSecond = Math.max(...seconds);
  const firstTick =
    Math.ceil(minSecond / TREND_TIME_TICK_SECONDS) * TREND_TIME_TICK_SECONDS;
  const ticks: number[] = [];

  for (
    let second = firstTick;
    second <= maxSecond;
    second += TREND_TIME_TICK_SECONDS
  ) {
    ticks.push(second);
  }

  return ticks;
};

export const buildSegmentSecondOptions = (rows: ChartRow[]) =>
  rows.slice(0, -1).map(row => ({
    value: row.second.toString(),
    label: formatSecondLabel(row.second),
  }));

export const buildSegmentEndOptions = (
  rows: ChartRow[],
  startValue: string | null
) => {
  const seconds = rows.map(row => row.second);
  if (seconds.length < 2) return [];
  if (!startValue) {
    return seconds.slice(1).map(second => ({
      value: second.toString(),
      label: formatSecondLabel(second),
    }));
  }
  const startSecond = Number(startValue);
  return seconds
    .filter(second => second > startSecond)
    .map(second => ({
      value: second.toString(),
      label: formatSecondLabel(second),
    }));
};

export const filterSegmentRows = (
  rows: ChartRow[],
  startValue: string | null,
  endValue: string | null
) => {
  if (!startValue && !endValue) return rows;
  const startSecond = startValue ? Number(startValue) : -Infinity;
  const endSecond = endValue ? Number(endValue) : Infinity;
  return rows.filter(
    row => row.second >= startSecond && row.second <= endSecond
  );
};

export const buildTrendStartOptions = (availableSeconds: number[]) => {
  if (availableSeconds.length < 2) return [];
  return availableSeconds.slice(0, -1).map(second => ({
    value: second.toString(),
    label: formatSecondLabel(second),
  }));
};

export const buildTrendEndOptions = (
  availableSeconds: number[],
  startValue: string | null
) => {
  if (availableSeconds.length < 2) return [];
  if (!startValue) {
    return availableSeconds.slice(1).map(second => ({
      value: second.toString(),
      label: formatSecondLabel(second),
    }));
  }

  const startSecond = Number(startValue);
  return availableSeconds
    .filter(second => second > startSecond)
    .map(second => ({
      value: second.toString(),
      label: formatSecondLabel(second),
    }));
};

export const formatTrendTooltipLabel = (value: number | string) =>
  formatSecondLabel(Number(value));

export const formatTrendTooltipMetric = (value: number | string) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'n/a';
  return numeric.toFixed(2);
};
